"use strict";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import {
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Input,
  MultiSelect,
  MultiSelectProps,
  Select,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDebouncedCallback, useDisclosure } from "@mantine/hooks";
import {
  IconBarcode,
  IconBrandBootstrap,
  IconCalendarStats,
  IconChartBar,
  IconChartDonut3,
  IconSearch,
  IconSortAscendingShapes,
  IconTable,
} from "@tabler/icons-react";
import { AgChartOptions } from "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";
import { ColDef, GridReadyEvent, ModuleRegistry } from "ag-grid-community";
import {
  IServerSideDatasource,
  IServerSideGetRowsRequest,
  ServerSideRowModelModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  paginationPageSize,
  paginationPageSizeSelector,
  repositoryDelivery,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { SelectListItemBase } from "../../../_base/model/_base/SelectListItemBase";
import { formatPrice } from "../../../common/formatPrice/PriceFunction";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { MessageResponse } from "../../../model/MessageResponse";

ModuleRegistry.registerModules([ServerSideRowModelModule]);

const SumListSerialV2 = () => {
  const [loading, setLoading] = useState(true);
  const gridRef = useRef<AgGridReact<any>>(null);
  const datasourceRef = useRef<IServerSideDatasource | null>(null);
  const [dataItemCode, setDataItemCode] = useState<any[]>([]);

  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);

  const [height, setHeight] = useState(800);
  const [dataBranchCode, setDataBranchCode] = useState<SelectListItemBase[]>(
    []
  );
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [visible, { toggle, close, open }] = useDisclosure(false);

  // Chỗ này để làm lọc dùng form cho đỡ lag so với state
  // Những tham số comment là những tham số chưa cần truyền , những tham số truyền nhưng không hiển thị là theo yêu cầu (vì cần truy vấn dữ liệu)
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const form = useForm<any>({
    initialValues: {
      BranchCode: null, // array[string]
      ItemCode: null, // array[string] //mã sản phẩm
      TimeType: "month", // month, quarter , year
      Month: currentMonth, // number
      Quarter: null, // number (default Q1)
      Year: currentYear, // number
      KeySearch: null, // string
      Tabs: "table", // table, chartColumn, chartPie
      view: "soluong", // soluong, giatri
    },
  });

  // Cho hình cột
  const ChartColumnRef = useRef<HTMLDivElement>(null);
  // Cho hình tròn
  const ChartPieRef = useRef<HTMLDivElement>(null);

  const containerStyle = useMemo(
    () => ({ width: "100%", height: `${height}px` }),
    [height]
  );
  const gridStyle = useMemo(
    () => ({ height: `${height + 90}px `, width: "100%" }),
    [height]
  );
  const sizeGrid = { base: 6, sm: 3, md: 3, lg: 2, xl: 2 };

  // Chart data state
  const [chartData, setChartData] = useState<any[]>([]);
  const chartOptions = useMemo<AgChartOptions>(
    () => ({
      autoSize: true,
      theme: "ag-default",
      title: {
        text:
          form.getValues().view === "giatri"
            ? `Tổng giá trị tồn kho theo chi nhánh`
            : `Tổng số lượng tồn kho theo chi nhánh`,
        fontSize: 22,
        color: "#000",
      },
      subtitle: {
        text: `Đơn vị: ${form.getValues().view === "giatri" ? "VND" : "Số lượng"
          }`,
        color: "#000",
      },
      data: chartData,
      series: [
        {
          type: "bar" as const,
          direction: "horizontal",
          xKey: "branch",
          yKey: form.getValues().view === "giatri" ? "revenue" : "orders",
          yName:
            form.getValues().view === "giatri"
              ? "Tổng giá trị"
              : "Tổng số lượng",
          fill: "#4dabf7",
          stroke: "#228be6",
          cornerRadius: 4,
          label: {
            enabled: true,
            color: "#000",
            fontSize: 13,
            placement: "outside-end",
            formatter: ({ value }: any) =>
              form.getValues().view === "giatri"
                ? formatPrice(value)
                : value.toString(),
            marginLeft: 10,
          },
        },
      ],
      axes: [
        {
          type: "category",
          position: "left",
          label: {
            color: "#000",
            maxWidth: 500,
            width: 500,
            autoRotate: false,
          },
        },
        {
          type: "number",
          position: "bottom",
          title: { text: "Số lượng ", color: "#000" },
          label: {
            formatter: (params: any) =>
              form.getValues().view === "giatri"
                ? formatPrice(params.value)
                : params.value.toString(),
            color: "#000",
          },
          gridLine: {
            style: [{ stroke: "#2c3340", lineDash: [4, 4] }],
          },
        },
      ],
      legend: { enabled: false },
      padding: { top: 20, right: 60, bottom: 20, left: 20 },
    }),
    [chartData, form.getValues().view]
  );

  // Chart Pie Options
  const pieOptions = useMemo<AgChartOptions>(
    () => ({
      autoSize: true,
      theme: "ag-default",
      title: {
        text: `Tỷ trọng tồn kho theo chi nhánh`,
        fontSize: 22,
        color: "#000",
      },
      data: chartData,
      series: [
        {
          type: "pie",
          angleKey: form.getValues().view === "giatri" ? "revenue" : "orders",
          legendItemKey: "branch",
          calloutLabelKey: "branch",
          sectorLabelKey:
            form.getValues().view === "giatri" ? "revenue" : "orders",
          calloutLabel: {
            enabled: true,
            color: "#000",
            fontSize: 12,
            offset: 10,
          },
          calloutLine: {
            strokeWidth: 1,
            colors: ["#000"],
          },
          tooltip: {
            renderer: ({ datum }: any) =>
              `${datum.branch} — ${form.getValues().view === "giatri"
                ? formatPrice(datum.revenue)
                : datum.orders
              }`,
          },
        },
      ],
      highlightStyle: {
        item: {
          strokeWidth: 5,
        },
        series: {
          opacity: 0.2,
        },
      },
      legend: {
        position: "bottom",
        item: {
          label: {
            color: "#000",
          },
          paddingX: 20,
        },
      },
      padding: { top: 20, right: 20, bottom: 20, left: 20 },
    }),
    [chartData, form.getValues().view]
  );

  useEffect(() => {
    const updateHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      // 400 là tổng khoảng cách cần trừ (breadcrumb, filters, padding, pagination)
      const newHeight = window.innerHeight - (210 + headerHeight);
      setHeight(newHeight);
    };

    // Đợi DOM render xong rồi mới tính
    setTimeout(updateHeight, 0);
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  // Hàm tự động set ngày theo loại kỳ
  const handleLoaiKyChange = useCallback(
    (value: string | null) => {
      const nowLocal = new Date();
      const monthNow = nowLocal.getMonth() + 1;
      const yearNow = nowLocal.getFullYear();

      form.setFieldValue("TimeType", value ?? "month");
      setSelectedPeriod(null); // Reset selected period when LoaiKy changes

      // Set sensible defaults for each type
      if (value === "month") {
        form.setFieldValue("Month", monthNow);
        form.setFieldValue("Year", yearNow);
        form.setFieldValue("Quarter", null);
      } else if (value === "quarter") {
        form.setFieldValue("Quarter", 1);
        form.setFieldValue("Year", yearNow);
        form.setFieldValue("Month", null);
      } else if (value === "year") {
        form.setFieldValue("Year", yearNow);
        form.setFieldValue("Month", null);
        form.setFieldValue("Quarter", null);
      }
    },
    [form]
  );

  // Lấy danh sách chi nhánh
  const fetchBranchCode = async () => {
    try {
      const response = await repositoryPos.get<
        MessageResponse<SelectListItemBase[]>
      >(`api/v1/TblDmInventory/get-select-branch?auth=true`);
      if (response && response.success) {
        const data = response.data;
        setDataBranchCode(data);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };

  // Hàm fetch dữ liệu data
  const fetchData = async (request: IServerSideGetRowsRequest) => {
    try {
      // Lấy page size hiện tại từ grid API
      const currentPageSize =
        gridRef.current?.api?.paginationGetPageSize() || paginationPageSize;
      const skip = request.startRow || 0;
      const take = currentPageSize;

      const keySearch = {
        KeySearch: form.getValues().KeySearch,
        InvCode: form.getValues().InvCode,
        startDate: form.getValues().startDate,
        endDate: form.getValues().endDate,
        TimeType: form.getValues().TimeType,
        Month: form.getValues().Month,
        Quarter: form.getValues().Quarter,
        Year: form.getValues().Year,
        InvId: form.getValues().InvId,
      };

      let url = `/api/v1/SumInventory/get-sum-list-serial?Skip=${skip}&Take=${take}`;
      // Xử lý BrandCode (hoặc BranchCode) là mảng
      if (
        form.getValues().BranchCode &&
        Array.isArray(form.getValues().BranchCode)
      ) {
        form.getValues().BranchCode.forEach((code: string) => {
          url += `&BranchCode=${encodeURIComponent(code)}`;
        });
      }
      // "LTAC0880;LTAU1016"
      // ItemCode: truyền dạng ; ngăn cách
      if (
        form.getValues().ItemCode &&
        Array.isArray(form.getValues().ItemCode) &&
        form.getValues().ItemCode.length > 0
      ) {
        form.getValues().ItemCode.forEach((code: string) => {
          url += `&ItemCode=${encodeURIComponent(code)}`;
        });
      }
      // Các param khác
      Object.entries(keySearch).forEach(([key, value]) => {
        if (key === "BranchCode" || key === "ItemCode") return; // tránh lặp lại
        // Loại bỏ chỉ khi value là null hoặc undefined
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v !== null && v !== undefined) {
                url += `&${key}=${encodeURIComponent(v)}`;
              }
            });
          } else {
            url += `&${key}=${encodeURIComponent(value)}`;
          }
        }
      });

      const resp = await repositoryDelivery.get(url);
      // Ensure we always return an array (empty if no data)
      return resp || [];
    } catch (error) {
      console.error(error);
      // On error return empty array so callers can handle empty result consistently
      return [];
    }
  };

  //#region Download Image
  const handleDownloadImage = async (
    refCurrent: React.RefObject<HTMLDivElement>
  ) => {
    const html2canvas = await import("html2canvas-pro");

    if (!refCurrent.current) {
      console.error("Error: refCurrent is null.");
      return;
    }

    // setIsLoading(true);
    open(); // Hiển thị loading

    try {
      // 🚀 TRICK 1: Pre-optimize DOM trước khi capture
      const element = refCurrent.current;

      // Set fixed size để html2canvas xử lý nhanh hơn
      const originalStyle = element.style.cssText;
      element.style.width = `${element.scrollWidth}px`;
      element.style.transform = "scale(1)";
      element.style.transformOrigin = "top left";

      // 🚀 TRICK 2: Hide unnecessary elements
      const unnecessaryElements = element.querySelectorAll(
        "button, .fa-solid, .fa-regular, svg"
      );
      const hiddenElements: HTMLElement[] = [];
      unnecessaryElements.forEach((el: any) => {
        if (el.style.display !== "none") {
          hiddenElements.push(el);
          el.style.display = "none";
        }
      });

      // 🚀 TRICK 3: Html2canvas với settings tối ưu tối đa
      const canvas = await html2canvas.default(element, {
        scale: 1, // 🔥 Key optimization: scale = 1
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        width: element.scrollWidth,
        logging: false,
        imageTimeout: 1000, // 🔥 Giảm timeout
        removeContainer: false,
        foreignObjectRendering: false,
        ignoreElements: (element) => {
          // 🔥 Skip heavy elements
          return (
            element.tagName === "BUTTON" ||
            element.tagName === "SVG" ||
            element.classList.contains("fa-solid") ||
            element.classList.contains("fa-regular")
          );
        },
      });

      // 🚀 TRICK 4: Restore DOM ngay lập tức
      element.style.cssText = originalStyle;
      hiddenElements.forEach((el) => {
        el.style.display = "";
      });

      // 🚀 TRICK 5: Generate filename nhanh
      const now = new Date();
      const fileName = `Bieu-Do-Bao-Cao-Ton-Kho-Theo-Chi-Nhanh-${form.getValues().Tabs === "chartColumn" ? "hinh-Cot" : "hinh-Tron"
        }-${String(now.getDate()).padStart(2, "0")}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}-${String(
          now.getMinutes()
        ).padStart(2, "0")}`;

      // 🚀 TRICK 6: Download trực tiếp bằng toBlob (nhanh nhất)
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${fileName}.jpg`;
            link.click();
            URL.revokeObjectURL(url);
            NotificationExtension.Success("Tải ảnh thành công!");
          }
        },
        "image/jpeg",
        0.85
      );
    } catch (error) {
      console.error("Error generating image:", error);
      NotificationExtension.Fails("Tải ảnh thất bại!");
    } finally {
      // setIsLoading(false);
      close(); // Đóng loading
    }
  };

  // Call api bên server
  const getServerSideDatasource = useCallback((): IServerSideDatasource => {
    return {
      getRows: async (params) => {
        const response = (await fetchData(params.request)) || [];
        const allData = Array.isArray(response) ? response : [];

        interface ChartRow {
          branch: string;
          revenue: number;
          orders: number;
          legend: string;
        }

        let chartRows: ChartRow[] = (
          allData as Array<{
            branchName?: string;
            totalSoLuong?: number;
            totalGiaTri?: number;
          }>
        ).map((r): ChartRow => {
          const branch = (r.branchName || "").replace("HACOM - ", "");
          const orders = Number(r.totalSoLuong) || 0;
          const revenue = Number(r.totalGiaTri) || 0;
          return {
            branch: `${branch}`,
            revenue,
            orders,
            legend: `${branch} (${isNaN(revenue) ? orders : revenue})`,
          };
        });

        // Sort according to current view
        {
          const view = form.getValues().view;
          const metricKey = view === "giatri" ? "revenue" : "orders";
          chartRows = chartRows.sort(
            (a, b) => (b as any)[metricKey] - (a as any)[metricKey]
          );
        }
        setChartData(chartRows);

        // Apply server-side sort fallback when needed
        const sortModel: any[] = (params.request as any).sortModel || [];
        const primarySort = sortModel.length
          ? { colId: sortModel[0].colId, sort: sortModel[0].sort }
          : form.getValues().Tabs !== "table"
            ? {
              colId:
                form.getValues().view === "giatri"
                  ? "totalGiaTri"
                  : "totalSoLuong",
              sort: "desc",
            }
            : null;
        if (primarySort?.colId) {
          const col = primarySort.colId;
          const dir = primarySort.sort === "asc" ? 1 : -1;
          (allData as any[]).sort((a, b) => {
            const av = Number(a?.[col]) || 0;
            const bv = Number(b?.[col]) || 0;
            return dir === 1 ? av - bv : bv - av;
          });
        }

        // Always call success (even with empty array) so grid and charts update
        params.success({
          rowData: allData,
          rowCount: allData.length,
        });
        setLoading(false);
      },
    };
  }, [form.getValues().BranchCode, form.getValues().Tabs]);
  // Sau khi chọn tìm kiếm xong thì sẽ thực hiện click để call api này
  const handleSearch = useCallback(() => {
    console.log("Search clicked with values:", form.getValues());
    setLoading(true);
    const api = gridRef.current?.api;
    if (!api) {
      console.log("API not ready");
      return;
    }

    // Refresh server-side cache để gọi lại getRows với filter mới
    (api as any).refreshServerSide({ purge: true });
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      // đăng datasource đúng để ag-Grid gọi getRows
      const datasource = getServerSideDatasource();
      datasourceRef.current = datasource;
      params.api.setGridOption("serverSideDatasource", datasource);
      console.log("Grid ready, datasource registered");
    },
    [getServerSideDatasource]
  );

  const getRowId = useCallback((params: any) => {
    return params.data?.branchCode || Math.random().toString();
  }, []);

  const getRowStyle = useCallback((params: any) => {
    if (params.node.rowPinned) {
      return {
        backgroundColor: "#e7f5ff",
        fontWeight: 700,
        fontSize: "14px",
        borderTop: "3px solid #15aabf",
      };
    }
    return undefined;
  }, []);

  const [columnDefs] = useState<ColDef[]>([
    { field: "branchCode", headerName: "Mã Chi Nhánh" },
    { field: "branchName", headerName: "Tên Chi Nhánh" },
    {
      field: "totalSoLuong",
      headerName: "Tổng số lượng",
      sortable: true,
      width: 300,
    },
    {
      field: "donGiaNhap",
      headerName: "Đơn Giá Nhập",
      width: 300,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : ""),
    },
    {
      field: "totalGiaTri",
      headerName: "Tổng Giá Trị",
      width: 300,
      sortable: true,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : ""),
    },
  ]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      resizable: true,
    };
  }, []);

  // Lấy dữ liệu mã sản phẩm
  const DataItemCode = async (keySearch: string) => {
    try {
      let url = "";
      if (keySearch && keySearch.length > 0) {
        url += `&ItemCodes=${keySearch}`;
      }
      const response = await repositoryPos.get(
        `/api/v1/TblItem/get-select?Key=${keySearch}&Page=30`
      );
      if (response && response.success) {
        const transformedData = response.data
          ?.filter((i: any) => i.value && i.text)
          .map((branch: any) => ({
            value: branch.value,
            label: branch.text,
          }));
        const uniqueData = Array.from(
          new Set(transformedData.map((item: any) => item.value))
        ).map((value) =>
          transformedData.find((item: any) => item.value === value)
        );
        setDataItemCode(uniqueData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };

  // Sử dụng debounce để trì hoãn việc gọi API
  const debouncedDataItemCode = useDebouncedCallback((value: string) => {
    if (value.length >= 3) {
      DataItemCode(value); // Gọi API với từ khóa tìm kiếm
    }
  }, 500);

  const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
    option,
  }) => (
    <Group gap="sm">
      <Text>{dataItemCode.find((x) => x.value === option.value)?.label}</Text>
    </Group>
  );

  useEffect(() => {
    fetchBranchCode();
  }, []);

  console.log("Loggform.getValue()", form.getValues());
  return (
    <Box pos={"relative"} style={containerStyle}>
      <LoadingOverlayCustom visible={visible} />
      <div
        style={{
          border: "1px solid #dee2e6",
          padding: "0px 10px",
          overflowX: "hidden",
        }}
      >
        <Group justify="space-between">
          <BreadcrumbLayout1></BreadcrumbLayout1>
        </Group>
      </div>
      <Tabs
        value={form.getValues().Tabs}
        onChange={(value) => {
          form.setFieldValue("Tabs", value);
          setLoading(true);
          // reload data theo loại mới
          gridRef.current?.api?.refreshServerSide?.({ purge: true });
        }}
      >
        <Flex justify={"space-between"} style={{ marginBottom: 12 }}>
          <Group align="end" flex={"1"} wrap="wrap">
            <Grid style={{ width: "100%" }} mt={10}>
              <Grid.Col span={sizeGrid}>
                <Input
                  placeholder="Từ khoá"
                  leftSection={<IconSearch color="#15aabf" />}
                  key={form.key("keySearch")}
                  {...form.getInputProps("KeySearch")}
                  styles={{
                    input: {
                      fontWeight: 600,
                      borderColor: "#15aabf",
                      borderWidth: "2px",
                    },
                  }}
                />
              </Grid.Col>
              <Grid.Col span={sizeGrid}>
                <Select
                  size="sm"
                  placeholder="Chọn loại kỳ"
                  leftSection={<IconCalendarStats size={20} color="#15aabf" />}
                  data={[
                    { value: "month", label: "Tháng" },
                    { value: "quarter", label: "Quý" },
                    { value: "year", label: "Năm" },
                  ]}
                  {...form.getInputProps("TimeType")}
                  onChange={handleLoaiKyChange}
                  styles={{
                    input: {
                      fontWeight: 600,
                      borderColor: "#15aabf",
                      borderWidth: "2px",
                    },
                  }}
                />
              </Grid.Col>

              {form.getValues().TimeType === "month" ? (
                <Grid.Col span={sizeGrid}>
                  <MonthPickerInput
                    size="sm"
                    placeholder="Chọn tháng"
                    locale="vi"
                    valueFormat="MM/YYYY"
                    clearable
                    value={
                      form.getValues().Month && form.getValues().Year
                        ? new Date(
                          form.getValues().Year,
                          form.getValues().Month - 1,
                          1
                        )
                        : new Date(currentYear, currentMonth - 1, 1)
                    }
                    onChange={(d) => {
                      if (d) {
                        form.setFieldValue("Month", d.getMonth() + 1);
                        form.setFieldValue("Year", d.getFullYear());
                      }
                    }}
                    leftSection={
                      <IconCalendarStats size={20} color="#15aabf" />
                    }
                    styles={{
                      input: {
                        fontWeight: 600,
                        borderColor: "#15aabf",
                        borderWidth: "2px",
                      },
                    }}
                  />
                </Grid.Col>
              ) : form.getValues().TimeType === "quarter" ? (
                <>
                  <Grid.Col span={sizeGrid}>
                    <Select
                      size="sm"
                      placeholder="Chọn quý"
                      data={[
                        { value: "1", label: "Quý 1 (Tháng 1-3)" },
                        { value: "2", label: "Quý 2 (Tháng 4-6)" },
                        { value: "3", label: "Quý 3 (Tháng 7-9)" },
                        { value: "4", label: "Quý 4 (Tháng 10-12)" },
                      ]}
                      value={
                        form.getValues().Quarter
                          ? String(form.getValues().Quarter)
                          : "1"
                      }
                      onChange={(v) =>
                        form.setFieldValue("Quarter", v ? Number(v) : null)
                      }
                      styles={{
                        input: {
                          fontWeight: 600,
                          borderColor: "#15aabf",
                          borderWidth: "2px",
                        },
                      }}
                    />
                  </Grid.Col>
                  <Grid.Col span={sizeGrid}>
                    <Select
                      size="sm"
                      placeholder="Chọn năm"
                      data={Array.from({ length: 5 }).map((_, i) => ({
                        value: String(currentYear - i),
                        label: String(currentYear - i),
                      }))}
                      value={
                        form.getValues().Year
                          ? String(form.getValues().Year)
                          : String(currentYear)
                      }
                      onChange={(v) =>
                        form.setFieldValue("Year", v ? Number(v) : null)
                      }
                      styles={{
                        input: {
                          fontWeight: 600,
                          borderColor: "#15aabf",
                          borderWidth: "2px",
                        },
                      }}
                    />
                  </Grid.Col>
                </>
              ) : (
                <Grid.Col span={sizeGrid}>
                  <Select
                    size="sm"
                    placeholder="Chọn năm"
                    leftSection={
                      <IconCalendarStats size={20} color="#15aabf" />
                    }
                    data={Array.from({ length: 5 }).map((_, i) => ({
                      value: String(currentYear - i),
                      label: String(currentYear - i),
                    }))}
                    value={
                      form.getValues().Year
                        ? String(form.getValues().Year)
                        : String(currentYear)
                    }
                    onChange={(v) =>
                      form.setFieldValue("Year", v ? Number(v) : null)
                    }
                    styles={{
                      input: {
                        fontWeight: 600,
                        borderColor: "#15aabf",
                        borderWidth: "2px",
                      },
                    }}
                  />
                </Grid.Col>
              )}

              {form.getValues().Tabs !== "table" && (
                <Grid.Col span={sizeGrid}>
                  {/* Sắp xếp theo  */}
                  <Select
                    size="sm"
                    leftSection={<IconSortAscendingShapes color="#15aabf" />}
                    placeholder="Xem theo"
                    data={[
                      { value: "soluong", label: "Tổng số lượng" },
                      { value: "giatri", label: "Tổng giá trị" },
                    ]}
                    // {...form.getInputProps("view")}
                    value={form.getValues().view}
                    onChange={(v) => {
                      form.setFieldValue("view", v);
                      const colId =
                        v === "giatri" ? "totalGiaTri" : "totalSoLuong";
                      // chỉ set sort model (không đổi thứ tự cột)
                      gridRef.current?.api?.applyColumnState({
                        state: [{ colId, sort: "desc" }],
                        applyOrder: false,
                      });
                      // reload server-side để lấy dữ liệu đã sắp xếp
                      gridRef.current?.api?.refreshServerSide?.({
                        purge: true,
                      });
                    }}
                    styles={{
                      input: {
                        fontWeight: 600,
                        borderColor: "#15aabf",
                        borderWidth: "2px",
                      },
                    }}
                    defaultValue="soluong"
                    allowDeselect={false}
                  />
                </Grid.Col>
              )}
              <Grid.Col span={sizeGrid}>
                <MultiSelect
                  size="sm"
                  leftSection={<IconBrandBootstrap color="#15aabf" />}
                  searchable
                  clearable
                  placeholder="Chọn chi nhánh"
                  data={dataBranchCode
                    .filter((ele) => ele.att2 && ele.text)
                    .map((ele) => ({
                      label: ele.att2 + " - " + ele.text,
                      value: ele.att2,
                    }))}
                  key={form.key("BranchCode")}
                  // {...form.getInputProps("BranchCode")}
                  onChange={(value) => form.setFieldValue("BranchCode", value)}
                  styles={{
                    input: {
                      fontWeight: 600,
                      borderColor: "#15aabf",
                      borderWidth: "2px",
                    },
                  }}
                />
              </Grid.Col>
              <Grid.Col span={sizeGrid}>
                <MultiSelect
                  size="sm"
                  placeholder="Nhập mã sản phẩm"
                  clearable
                  searchable
                  data={dataItemCode}
                  renderOption={renderMultiSelectOption}
                  value={form.getValues().ItemCode || []}
                  onSearchChange={(value) => {
                    debouncedDataItemCode(value); // Sử dụng debounce
                  }}
                  onChange={(v) => form.setFieldValue("ItemCode", v)}
                  leftSection={<IconBarcode size={20} color="#15aabf" />}
                  styles={{
                    input: {
                      fontWeight: 600,
                      borderColor: "#15aabf",
                      borderWidth: "2px",
                    },
                  }}
                />
              </Grid.Col>
              <Grid.Col span={sizeGrid}>
                <Button
                  leftSection={<IconSearch size={20} />}
                  color="blue"
                  variant="filled"
                  onClick={handleSearch}
                  styles={{
                    root: {
                      fontWeight: 600,
                    },
                  }}
                >
                  Tìm kiếm
                </Button>
              </Grid.Col>
            </Grid>
          </Group>
          <Group>
            <Tabs.List>
              {/* Biểu đồ hình cột */}
              <Tabs.Tab value="chartColumn">
                <IconChartBar size={24} />
              </Tabs.Tab>
              {/* Biểu đồ hình tròn */}
              <Tabs.Tab value="PieChart">
                <IconChartDonut3 size={24} />
              </Tabs.Tab>
              {/* table bình thường */}
              <Tabs.Tab value="table">
                <IconTable size={24} />
              </Tabs.Tab>
            </Tabs.List>
          </Group>
        </Flex>
        <Tabs.Panel value="table" style={{ height: `${height}px !important` }}>
          <div
            // style={{ height: `650px` }}
            style={gridStyle}
            className={"ag-theme-quartz"}
          >
            <AgGridReact<any>
              ref={gridRef}
              loading={loading}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowModelType={"serverSide"}
              pagination={true}
              paginationPageSize={100}
              paginationPageSizeSelector={paginationPageSizeSelector}
              onGridReady={onGridReady}
              getRowId={getRowId}
              getRowStyle={getRowStyle}
              localeText={AG_GRID_LOCALE_VN}
            // pinnedBottomRowData={totalRow ? [totalRow] : []}
            />
          </div>
        </Tabs.Panel>

        <Tabs.Panel
          value="PieChart"
          pt="xs"
          style={{ height: `${height + 700}px` }}
        >
          <Group justify="end" mt="md">
            <Button
              color="teal"
              onClick={() => handleDownloadImage(ChartPieRef)}
            >
              Tải ảnh biểu đồ tròn
            </Button>
          </Group>
          <div
            ref={ChartPieRef}
            style={{
              width: "100%",
            }}
          >
            {chartData && chartData.length > 0 ? (
              <AgCharts
                options={pieOptions}
                style={{ width: "100%", height: "750px" }}
              />
            ) : (
              <Box
                style={{
                  width: "100%",
                  height: "750px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed #e9ecef",
                  color: "#666",
                }}
              >
                Không có dữ liệu
              </Box>
            )}
          </div>
        </Tabs.Panel>

        {/* Hiển thị dạng dạng cột nằm ngang  */}
        <Tabs.Panel
          value="chartColumn"
          //  style={{ height: `${height + 700}px` }}
          style={{ height: `auto` }}
        >
          <Group justify="end" mt="md">
            <Button
              color="teal"
              onClick={() => handleDownloadImage(ChartColumnRef)}
            >
              Tải ảnh biểu đồ cột
            </Button>
          </Group>
          <div
            ref={ChartColumnRef}
            style={{
              width: "100%",
            }}
          >
            {chartData && chartData.length > 0 ? (
              <AgCharts
                options={chartOptions}
                className="chart"
                style={{ width: "100%", height: `1400px`, overflowX: "hidden" }}
              />
            ) : (
              <Box
                style={{
                  width: "100%",
                  height: `750px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed #e9ecef",
                  color: "#666",
                }}
              >
                Không có dữ liệu
              </Box>
            )}
          </div>
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
};
export default SumListSerialV2;
